/**
 * @file settings.store.ts
 *
 * Zustand store for shop-wide display settings shared across tabs. For now it
 * holds only the active currency symbol used to format prices. The symbol is
 * decorative, so a failed fetch keeps the existing fallback rather than
 * blocking the screens; later phases extend this store with editable settings.
 *
 * The symbol is fetched once for the whole admin (from {@link AdminLayout}) and
 * read by every tab, so `fetchCurrencySymbol` guards against repeat loads.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { fetchCurrencySymbol as apiFetchCurrencySymbol } from '../api/settings';

/** Symbol shown before prices until the store loads the real one. */
export const DEFAULT_CURRENCY_SYMBOL = '$';

/** Shape of the settings store. */
interface SettingsState {
  currencySymbol: string;
  /** Whether the currency symbol has been requested this session. */
  currencyLoaded: boolean;
  /** Loads the active currency symbol once; failures keep the current value. */
  fetchCurrencySymbol: () => Promise<void>;
}

export const useSettingsStore = create<SettingsState>()(
  devtools(
    (set, get) => ({
      currencySymbol: DEFAULT_CURRENCY_SYMBOL,
      currencyLoaded: false,

      fetchCurrencySymbol: async () => {
        // Load once per session; set the flag before awaiting so concurrent callers don't double-fetch.
        if (get().currencyLoaded) {
          return;
        }
        set({ currencyLoaded: true }, false, 'settings/currency/pending');
        try {
          const symbol = await apiFetchCurrencySymbol();
          if (symbol) {
            set({ currencySymbol: symbol }, false, 'settings/currency/fulfilled');
          }
        } catch {
          // Currency symbol is decorative; keep the fallback on failure.
        }
      },
    }),
    { name: 'shop/settings' },
  ),
);
